/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetServerSideProps } from 'next';

import { PageRendererComponent, getProcessEnvs, prePageCall } from '@marriott/mi-headless-utils';
import { fetchCFData, buildPatternSlots, mergeCfAndDcaToSlots } from '@marriott/mi-homepage-components';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
import { envVarKeys } from '../_constants/envVarKeys';
import operationSignatures from '../_constants/operationSignatures.json';
import homepageMockData from '../mock/polaris-logged-out.json';

export default function pageRenderer(props: any) {
  return <PageRendererComponent {...props} />;
}

/** Header injected by the edge when the request belongs to the Polaris A/B cohort. */
const POLARIS_AB_HEADER = 'polarishp';

const homepageSpecificData = async (context?: { [key: string]: any }, parallelCallsResponse?: any) => {
  const {
    NEXT_PUBLIC_SUBMIT_SEARCH_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY = null,
    UXL_IMAGE_CACHE_DOMAIN = null,
    SESSION_GET_CALL_URL_CLIENT,
    SESSION_APP_GET_CALL_V1_URL,
    DEPLOYED_ENV_CONFIG,
    TARGET_PROPERTY_TOKEN,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    SESSION_APP_POST_CALL_URL,
    INJECT_BRAND_CSS,
    NEXT_PUBLIC_PREFIX,
    GET_DESTINATION_TYPE_FLAG,
  } = getProcessEnvs();

  // Feature flag call for logo
  const featureFlagLogo = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  // Feature flag call to get cardCarousel flag
  const featureFlags = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_CAROUSEL_LIGHTINING_VARIANTS, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  // Feature flag to control deferred hydration optimization
  const featureFlagDeferHydration = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_HOMEPAGE_DEFER_HYDRATION, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  const featureFlagLegacyNav = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LEGACY_NAV, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  //Fetch concurFlow feature flag
  const featureFlagEnableConcurFlow = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ENABLE_CONCUR_FLOW, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
    enableStream: true,
  });
  const polarisHeader = context?.req?.headers?.[POLARIS_AB_HEADER];
  const isPolarisVariant = polarisHeader !== undefined && polarisHeader !== null && polarisHeader !== '';
  const isAuthenticated = parallelCallsResponse?.sessionData?.authenticated ?? false;

  return {
    ...(isPolarisVariant && {
      EXPERIENCE_SEGMENT: [isAuthenticated ? 'Logged In' : 'NOT Logged In', 'Polaris'],
    }),
    operationSignatures,
    productSpecificResponse: [],
    NEXT_PUBLIC_SUBMIT_SEARCH_URL: NEXT_PUBLIC_SUBMIT_SEARCH_URL ?? null,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: NEXT_PUBLIC_AVAILABILITY_SEARCH_URL ?? null,
    // Next.js cannot serialize `undefined` in props; ensure we pass null when unset.
    NEXT_PUBLIC_FETCH_OFFERS_API_URL: NEXT_PUBLIC_FETCH_OFFERS_API_URL ?? null,
    TARGET_PROPERTY_TOKEN: TARGET_PROPERTY_TOKEN ?? null,
    GOOGLE_MAP_API_KEY: GOOGLE_MAP_API_KEY ?? null,
    BAIDU_MAP_API_KEY,
    UXL_IMAGE_CACHE_DOMAIN,
    SESSION_GET_CALL_URL_CLIENT: SESSION_GET_CALL_URL_CLIENT ?? null,
    SESSION_APP_GET_CALL_V1_URL: SESSION_APP_GET_CALL_V1_URL ?? null,
    SESSION_APP_POST_CALL_URL: SESSION_APP_POST_CALL_URL ?? null,
    DEPLOYED_ENV_CONFIG: DEPLOYED_ENV_CONFIG ?? null,
    INJECT_BRAND_CSS: INJECT_BRAND_CSS ?? null,
    buildInfo: {
      deployedEnvType: process.env.DEPLOYED_ENV_TYPE ?? null,
      deployedEnvConfig: process.env.DEPLOYED_ENV_CONFIG ?? null,
      jenkinsDeploymentBuild: process.env.JENKINS_DEPLOYMENT_BUILD ?? null,
    },
    featureFlagLogo: featureFlagLogo ?? null,
    featureFlagDeferHydration: featureFlagDeferHydration ?? null,
    NEXT_PUBLIC_PREFIX: NEXT_PUBLIC_PREFIX ?? null,
    featureFlags: featureFlags ?? null,
    nextPublicEnvKeys: [...Object.keys(envVarKeys)],
    GET_DESTINATION_TYPE_FLAG: GET_DESTINATION_TYPE_FLAG ?? null,
    featureFlagLegacyNav: featureFlagLegacyNav ?? null,
    featureFlagEnableConcurFlow: featureFlagEnableConcurFlow ?? null,
  };
};

/**
 * Ensure SSR props are JSON-safe for Next.js by replacing nested `undefined`
 * values with `null`.
 */
const sanitizeForSsr = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value, (_key, currentValue) => (currentValue === undefined ? null : currentValue))) as T;

/**
 * This function is called on the server side before the page is rendered.
 * @param _context - The context object containing the request and response objects.
 * @param _parallelCallsResponse - The parallel calls response object.
 * @param _interceptorResponse - The interceptor response object.
 * @param model - The model object containing the page data.
 * @returns The updated model object.
 */
const postAEMModelOperations = async (
  context: any,
  parallelCallsResponse: any,
  _interceptorResponse: any,
  model: any
) => {
  try {
    console.log('Server Side: postAEMModelOperations started');
    const gridItems = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'] || {};

    const tl = gridItems?.tabbedlayout || gridItems?.dynamictabbedlayout;
    const isNorthStarPage = !!tl && (typeof tl === 'object' ? true : !!tl);
    model.isNorthStarPage = isNorthStarPage;

    const polarisHeader = context?.req?.headers?.[POLARIS_AB_HEADER];
    const isPolarisVariant = polarisHeader !== undefined && polarisHeader !== null && polarisHeader !== '';
    model.pageVariant = isPolarisVariant ? 'polaris' : 'bau';

    // TODO: This is as of now done for Polaris POC work. It can be better optimized in the future.
    const dynamictabbedlayoutComp = gridItems['dynamictabbedlayout'];
    if (dynamictabbedlayoutComp) {
      const hasNgSearchForm = Object.values(gridItems).some(
        (comp: any) => typeof comp?.[':type'] === 'string' && comp[':type'].includes('ngsearchform')
      );
      dynamictabbedlayoutComp.isNextGenSearchForm = hasNgSearchForm;
      const tabDetails: any[] = dynamictabbedlayoutComp.tabDetails || [];
      if (!tabDetails.length) return model;

      const queryTab: string | undefined = context?.query?.tab as string;
      const chosenTab = tabDetails.find(t => (t.tabName || '').toLowerCase() === (queryTab || '').toLowerCase());
      const tabToRender = chosenTab ?? tabDetails[0];
      const bannerFirst = Boolean(tabToRender.bannerFirst);
      const squareFirst = tabToRender.squareFirst !== false;
      const dcaTiles = tabToRender.DCAtiles || tabToRender.dcaTilesList || [];
      const slotsMeta = buildPatternSlots(bannerFirst, squareFirst, dcaTiles);
      const userState = parallelCallsResponse?.sessionData?.authenticated ?? false;
      const cf = await fetchCFData(tabToRender.tabName, false, userState);
      const mergedSlots = mergeCfAndDcaToSlots(slotsMeta, dcaTiles, cf);

      dynamictabbedlayoutComp.requestedTabData = sanitizeForSsr(mergedSlots);
      dynamictabbedlayoutComp.requestedTab = tabToRender.tabName;
    }
  } catch {
    //silent failure
  }
  return model;
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  homepageSpecificData,
  homepageMockData || {},
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
